import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { succesToast, errorToast } from 'basics/utils/toast';
import { Format } from 'generated/event.types';
import { Event } from 'modules/events/basics/types/events.types';
import { OnlineMeetingInput } from 'modules/events/basics/types/onlineMeeting.types';
import { eventFragment } from 'modules/events/graphql/fragments/event.fragments';
import { onlineEventDataFragment } from 'modules/events/graphql/fragments/onlineEventData.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const updateOnlineMeetingMutationCall = gql`
mutation updateOnlineMeetingMutation($onlineEventInput: OnlineEventInput!, $onlineEventFormat: Format) {
  updateOnlineEvent(onlineEventInput: $onlineEventInput, onlineEventFormat: $onlineEventFormat) {
    ${eventFragment}
    source
    data {
      ...on OnlineEvent {
        ${onlineEventDataFragment}
      }
    }
  }
}
`;

type ResultType = {
  updateOnlineEvent: Event;
};

type UseUpdateOnlineMeetingMutation = [
  (onlineEventInput: OnlineMeetingInput, onlineEventFormat?: Format) => Promise<Event | undefined | null>,
  MutationResult<ResultType>,
];

export const useUpdateOnlineMeetingMutation = (): UseUpdateOnlineMeetingMutation => {
  const { t } = useTranslation();
  const [updateOnlineMeeting, state] = useMutation<ResultType>(updateOnlineMeetingMutationCall);
  const executeMutation = useCallback(async (onlineEventInput: OnlineMeetingInput, onlineEventFormat?: Format) => {
    try {
      const data = await updateOnlineMeeting({
        variables: { onlineEventInput, onlineEventFormat },
      });
      succesToast(t('toast_draft_saved'));

      return data.data?.updateOnlineEvent;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, onlineEventInput);
        Logger.logError({
          error,
          customContext,
          transactionName: 'updateOnlineMeeting.ts - useUpdateOnlineMeetingMutation',
        });
      }
      errorToast(t('toast_draft_saved_error'));
      return null;
    }
  }, [updateOnlineMeeting, t]);

  return [executeMutation, state];
};
